import React, { useEffect, useState, useRef } from 'react'
import { useActivityType } from '../Hooks/ActivityTypeContext.js'
import PropTypes from 'prop-types'
import InputGroup from '../../EmailSubscriptionCenter/SignIn/InputGroup.jsx'
import { postMFA, postUpdateUsrContact } from '../Data/LoginDatasource.js'
import {
  FORM_NAME_CHALLENGE_CHOICE,
  FORM_NAME_LOGIN_FORM,
  FORM_NAME_VERIFY_PHONE_NUMBER,
} from '../Helpers/LoginHelper.js'
import {
  makeMfaAnswerCall,
  makeSubscribeUserCall,
} from '../Helpers/MfaHelper.js'
import FormNotification from '../../SelfService/FormNotification.jsx'
import {
  addTMXScriptToBody,
  generateUID,
} from '../../../spa/utility/threatMetrix/TMX.js'

function OneTimePasscode({
  otpData,
  handleDisplayChange,
  mode = 'otp',
  loginInputHook,
  passwordInputHook,
  phoneNumber = '',
  websession_id,
  setLoading,
}) {
  const [oneTimePasscodeInput, setOneTimePasscodeInput] = useState('')
  const [formValidation, setFormValidation] = useState({
    'login-one-time-passcode': {
      error: false,
      errorMessage:
        'The passcode you entered is incorrect or has expired. Please check the code you received and try again.',
    },
  })
  const [loginInput] = loginInputHook ? loginInputHook : [null]
  const [passwordInput] = passwordInputHook ? passwordInputHook : [null]
  const [formErrorFlag, setFormErrorFlag] = useState(false)
  const [formErrorMessage, setFormErrorMessage] = useState('')
  const [apitoken, setApiToken] = useState(null)

  const activityType = useActivityType()?.activityType || null
  const isClickedRef = useRef(false)

  const RESPONSE_CODES = {
    CREDENTIALS_NOT_ACCEPTED: '7001-3023',
    SSO_LOCKED: '7001-2029',
    BLOCKED_MFA: '7001-3027',
    LOCKED_MFA: '7001-3027',
    OTP_EXPIRED: '7001-3024',
    FYP_INVALID_CODE: 'CTN_FORGOTPASSWORD_1117',
    SUBSCRIBE_USER_INVALID_OTP: 'CTI_ADV_REG_SUBSCRIBEUSER_0004',
    SUBSCRIBE_USER_INVALID_OTP_2: '7083-3023',
  }

  const updateUsrContactHandler = async () => {
    setLoading(true)
    let _otpData = { ...otpData.updateUsrContact }
    const new_tmx_id = `cti_adv_${generateUID()}`
    try {
      await addTMXScriptToBody(new_tmx_id)
      const response = await postUpdateUsrContact(
        _otpData,
        new_tmx_id,
        websession_id
      )
      const apiTokenValue = response.headers?.get('apitoken')
      if (apiTokenValue) {
        setApiToken(apiTokenValue)
      }
    } catch (error) {
      console.error('Error updating user contact:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (otpData.updateUsrContact) {
      updateUsrContactHandler()
    } else if (otpData.id) {
      postMFA(otpData.id, activityType)
    }
  }, [otpData])

  const onChangeOneTimePasscodeInput = (event) => {
    setOneTimePasscodeInput(event.target.value)
  }

  const continueUpdateContactFlow = async (updateUsrContact) => {
    var loginPayload = {
      action: 'login',
      tokenNbr: null,
      emailId: loginInput,
      passcode: oneTimePasscodeInput,
      passwordTxt: passwordInput,
      isNewPhnNbr: updateUsrContact.isNewPhnNbr,
    }

    if (updateUsrContact.isNewPhnNbr) {
      loginPayload.usrVerifyPhoneNbr = updateUsrContact.usrVerifyPhoneNbr
    }

    const new_tmx_id = `cti_adv_${generateUID()}`
    try {
      await addTMXScriptToBody(new_tmx_id)
    } catch (error) {
      console.error('Error adding TMX script:', error)
    }
    await makeSubscribeUserCall(
      apitoken,
      loginPayload,
      new_tmx_id,
      websession_id,
      handleDisplayChange,
      handleError,
      setLoading
    )
  }

  const continueRegistrationFlow = async (registrationData) => {
    var registrationPayload = {
      action: 'registration',
      tokenNbr: registrationData.tokenNbr,
      emailId: null,
      passcode: oneTimePasscodeInput,
      passwordTxt: registrationData.password,
    }

    const new_tmx_id = `cti_adv_${generateUID()}`
    try {
      await addTMXScriptToBody(new_tmx_id)
    } catch (error) {
      console.error('Error adding TMX script:', error)
    }
    await makeSubscribeUserCall(
      registrationData.apiToken,
      registrationPayload,
      new_tmx_id,
      websession_id,
      handleDisplayChange,
      handleError,
      setLoading
    )
  }

  const continueClicked = () => {
    if (!oneTimePasscodeInput) {
      const _formValidation = { ...formValidation }
      _formValidation['login-one-time-passcode'].error = true
      setFormValidation(_formValidation)
    } else if (otpData.registrationData && !isClickedRef.current) {
      isClickedRef.current = true
      setLoading(true)
      continueRegistrationFlow(otpData.registrationData).finally(() => {
        isClickedRef.current = false
        setLoading(false)
      })
    } else if (otpData.updateUsrContact && !isClickedRef.current) {
      isClickedRef.current = true
      setLoading(true)
      continueUpdateContactFlow(otpData.updateUsrContact).finally(() => {
        isClickedRef.current = false
        setLoading(false)
      })
    } else if (otpData.id) {
      var data = {
        otp: oneTimePasscodeInput,
      }
      makeMfaAnswerCall(
        data,
        loginInput,
        mode,
        handleDisplayChange,
        handleError,
        activityType
      )
    }
  }

  const cancelClicked = () => {
    if (isClickedRef.current) return
    setOneTimePasscodeInput('')
    if (otpData.registrationData) {
      window.location.reload()
    } else {
      handleDisplayChange(FORM_NAME_LOGIN_FORM)
    }
  }

  const onDidntReceiveCodeClick = () => {
    setOneTimePasscodeInput('')
    if (otpData.registrationData) {
      handleDisplayChange('no-code-received')
    } else if (otpData.updateUsrContact) {
      handleDisplayChange(FORM_NAME_VERIFY_PHONE_NUMBER)
    } else {
      handleDisplayChange(FORM_NAME_CHALLENGE_CHOICE)
    }
  }

  const handleError = function (errorCode) {
    const _formValidation = { ...formValidation }
    switch (errorCode) {
      case RESPONSE_CODES.CREDENTIALS_NOT_ACCEPTED:
      case RESPONSE_CODES.OTP_EXPIRED:
      case RESPONSE_CODES.FYP_INVALID_CODE:
      case RESPONSE_CODES.SUBSCRIBE_USER_INVALID_OTP:
      case RESPONSE_CODES.SUBSCRIBE_USER_INVALID_OTP_2:
        _formValidation['login-one-time-passcode'].error = true
        _formValidation['login-one-time-passcode'].errorMessage =
          'The passcode you entered is incorrect or has expired. Please check the code you received and try again.'
        setFormValidation(_formValidation)
        break
      case RESPONSE_CODES.SSO_LOCKED:
      case RESPONSE_CODES.BLOCKED_MFA:
      case RESPONSE_CODES.LOCKED_MFA:
        setFormErrorFlag(true)
        setFormErrorMessage(
          'For your protection, your account has been locked after several failed attempts. To unlock your account, please <a href="/about-us/contact-us/">contact a Columbia Threadneedle</a> representative at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> for assistance.'
        )
        break
      default:
        setFormErrorFlag(true)
        setFormErrorMessage(
          'A problem occured with your account. Please call us at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> or check your email for further instruction.'
        )
    }
  }

  return (
    <div
      className="u-bgColorLightest u-borderColorNeutralLight u-borderLeft0 u-borderRight0"
      data-analytics-login-step="Enter OTP"
    >
      <div className="Grid">
        <div className="u-sizeFull u-lg-size5of12 u-lg-after1of12 u-marginBottom6gu u-lg-marginBottom0 Content">
          <div>
            <h2 className="u-marginTop0 u-marginBottom1gu">PASSCODE SENT</h2>
            <p className="u-marginBottom2gu">
              It may take up to 60 seconds for your passcode to arrive. Enter
              the 6-digit passcode we sent to XXX.XXX.{phoneNumber.slice(-4)}.
            </p>
          </div>
          <p>Your code:</p>
          <ul className="List u-marginBottom8gu">
            <li className="List-item u-textPrimary u-marginBottom0 u-padding0">
              Expires after 30 minutes
            </li>
            <li className="List-item u-textPrimary u-padding0">
              Does not replace your current password
            </li>
          </ul>
          {formErrorFlag && (
            <FormNotification content={formErrorMessage} isHtml={true} />
          )}
          <InputGroup
            handleInputChange={onChangeOneTimePasscodeInput}
            formValidation={formValidation}
            fieldName="login-one-time-passcode"
            fieldId="login-one-time-passcode"
            placeholder="Enter the passcode you received"
            label="One-time passcode"
            compact={false}
          />

          <div className="u-flex u-marginBottom3gu">
            <div className="u-flex">
              <button
                onClick={continueClicked}
                className="Button Button--primary Button--block u-marginRight5gu u-textSecondary u-paddingHoriz8gu u-textNoTransform"
                data-analytics="login-otp-continue"
              >
                Next
              </button>
              <button
                onClick={cancelClicked}
                className="Button Button--tertiary Button--block u-paddingHoriz8gu u-textNoTransform"
                data-analytics="login-otp-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- CTINEXT-4698 */}
          <a
            href="#"
            onClick={onDidntReceiveCodeClick}
            className="Link u-block"
            data-analytics="login-otp-no-code"
          >
            Didn&apos;t receive your code?
          </a>
        </div>
      </div>
    </div>
  )
}

OneTimePasscode.propTypes = {
  handleDisplayChange: PropTypes.func,
}

export default OneTimePasscode
